import {
  ChangeDetectorRef,
  Component,
  effect,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  Output,
  signal,
  untracked,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { DropdownModule } from 'primeng/dropdown';
import { SharedModule } from 'primeng/api';
import { Colorset } from '../../../models/colorset';
import { ColorsComponent } from '../colors/colors.component';
import { ColorsetStore } from '../../../stores/colorset/colorset.state';
import { FormFieldWrapperComponent } from '../../molecules/form-field-wrapper/form-field-wrapper.component';
import { MultiSelectModule } from 'primeng/multiselect';
import { Button } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { GlobalsStore } from '../../../stores/globals.state';
import { ColorsetFallback } from '../../../services/colorset/colorset-defaults.service';

@Component({
  selector: 'app-colorset-dropdown',
  standalone: true,
  imports: [
    DropdownModule,
    SharedModule,
    FormsModule,
    ColorsComponent,
    ReactiveFormsModule,
    FormFieldWrapperComponent,
    MultiSelectModule,
    Button,
    DialogModule,
  ],
  templateUrl: './colorset-dropdown.component.html',
  styleUrl: './colorset-dropdown.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ColorsetDropdownComponent),
      multi: true,
    },
  ],
})
export class ColorsetDropdownComponent implements ControlValueAccessor {
  readonly globalsStore = inject(GlobalsStore);
  readonly colorsetStore = inject(ColorsetStore);

  @Input() label = 'Farbset';
  @Input() name = 'colorset';
  /** Selected colorset id (bound to the dropdown via `optionValue="_id"`). */
  @Input() selection: string | null = null;
  @Input() disabled = false;
  @Input() colSize: string | number = 12;
  @Input() enableNewOption = false;
  @Input() placeholder?: string;
  /**
   * Colorset that takes effect while nothing is selected. Shown as a hint
   * below the field (and as placeholder) so the inherited value is visible.
   */
  @Input() fallback: ColorsetFallback | null = null;
  @Output() selectionChange = new EventEmitter<Colorset | null>();

  touched = false;
  colorsets: Colorset[] = [];

  /**
   * Set when this dropdown opened the "add colorset" dialog. Only then the
   * newly inserted (= selected) colorset is adopted - selecting a colorset
   * elsewhere (e.g. editing it) must not change this dropdown's value.
   */
  private awaitingNewColorset = false;

  constructor() {
    effect(() => {
      this.colorsets = this.colorsetStore.asArray();
    });

    effect(() => {
      const selectedId = this.colorsetStore.selectedId();
      if (!this.awaitingNewColorset || !selectedId) return;

      untracked(() => {
        this.awaitingNewColorset = false;
        this.selection = selectedId;
        this.onChange(selectedId);
        this.selectionChange.emit(this.colorsetStore.getById(selectedId));
      });
    });
  }

  onChange = (value: string | null) => {
    setTimeout(() => {
      this.selectionChange.emit(
        value ? this.colorsetStore.getById(value) : null
      );
    }, 100);
  };

  onDropdownChange(value: string | null) {
    this.selectionChange.emit(value ? this.colorsetStore.getById(value) : null);
  }

  // Call this method whenever the selection changes
  onSelectionChange(event: any): void {
    this.selection = event.value;
    this.onChange(this.selection); // Notify the form control of the change
    this.onTouched();
  }

  private onTouched = () => {
    this.touched = false;
  };

  writeValue(value: string | null): void {
    // '' (legacy "no colorset") would count as filled: clear icon, raised label
    this.selection = value || null;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  openColorsetForm() {
    this.awaitingNewColorset = true;
    this.globalsStore.triggerAtlasEvent('addColorsetDialog', {});
  }
}
